import { zodResolver } from '@hookform/resolvers/zod';
import { camelCase } from 'lodash';
import { useForm, UseFormProps } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import useWindowSize from '@rehooks/window-size';

import _useResizeObserver from 'use-resize-observer';
import { FieldError, Maybe } from '../components/RatesTool/types';

export const classNames: typeof twMerge = twMerge;
export const useResizeObserver = _useResizeObserver;

export type DeepNullablePartial<T> = {
  [K in keyof T]: DeepNullablePartial<T[K]> | null | undefined;
};

export function formatCurrency(
  value: string | number,
  options?: Intl.NumberFormatOptions
): string {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...options,
  });
  return currencyFormatter.format(Number(value));
}

export function useZodForm<TSchema extends z.ZodType>(
  props: Omit<UseFormProps<TSchema['_input']>, 'resolver'> & {
    schema: TSchema;
  }
) {
  const form = useForm<TSchema['_input']>({
    ...props,
    resolver: zodResolver(props.schema, undefined),
  });

  return form;
}

export const states: { name: string; code: string }[] = [
  {
    name: 'Alabama',
    code: 'AL',
  },
  {
    name: 'Alaska',
    code: 'AK',
  },
  {
    name: 'Arizona',
    code: 'AZ',
  },
  {
    name: 'Arkansas',
    code: 'AR',
  },
  {
    name: 'California',
    code: 'CA',
  },
  {
    name: 'Colorado',
    code: 'CO',
  },
  {
    name: 'Connecticut',
    code: 'CT',
  },
  {
    name: 'Delaware',
    code: 'DE',
  },
  {
    name: 'District of Columbia',
    code: 'DC',
  },
  {
    name: 'Florida',
    code: 'FL',
  },
  {
    name: 'Georgia',
    code: 'GA',
  },
  {
    name: 'Hawaii',
    code: 'HI',
  },
  {
    name: 'Idaho',
    code: 'ID',
  },
  {
    name: 'Illinois',
    code: 'IL',
  },
  {
    name: 'Indiana',
    code: 'IN',
  },
  {
    name: 'Iowa',
    code: 'IA',
  },
  {
    name: 'Kansas',
    code: 'KS',
  },
  {
    name: 'Kentucky',
    code: 'KY',
  },
  {
    name: 'Louisiana',
    code: 'LA',
  },
  {
    name: 'Maine',
    code: 'ME',
  },
  {
    name: 'Maryland',
    code: 'MD',
  },
  {
    name: 'Massachusetts',
    code: 'MA',
  },
  {
    name: 'Michigan',
    code: 'MI',
  },
  {
    name: 'Minnesota',
    code: 'MN',
  },
  {
    name: 'Mississippi',
    code: 'MS',
  },
  {
    name: 'Missouri',
    code: 'MO',
  },
  {
    name: 'Montana',
    code: 'MT',
  },
  {
    name: 'Nebraska',
    code: 'NE',
  },
  {
    name: 'Nevada',
    code: 'NV',
  },
  {
    name: 'New Hampshire',
    code: 'NH',
  },
  {
    name: 'New Jersey',
    code: 'NJ',
  },
  {
    name: 'New Mexico',
    code: 'NM',
  },
  {
    name: 'New York',
    code: 'NY',
  },
  {
    name: 'North Carolina',
    code: 'NC',
  },
  {
    name: 'North Dakota',
    code: 'ND',
  },
  {
    name: 'Ohio',
    code: 'OH',
  },
  {
    name: 'Oklahoma',
    code: 'OK',
  },
  {
    name: 'Oregon',
    code: 'OR',
  },
  {
    name: 'Pennsylvania',
    code: 'PA',
  },
  {
    name: 'Rhode Island',
    code: 'RI',
  },
  {
    name: 'South Carolina',
    code: 'SC',
  },
  {
    name: 'South Dakota',
    code: 'SD',
  },
  {
    name: 'Tennessee',
    code: 'TN',
  },
  {
    name: 'Texas',
    code: 'TX',
  },
  {
    name: 'Utah',
    code: 'UT',
  },
  {
    name: 'Vermont',
    code: 'VT',
  },
  {
    name: 'Virginia',
    code: 'VA',
  },
  {
    name: 'Washington',
    code: 'WA',
  },
  {
    name: 'West Virginia',
    code: 'WV',
  },
  {
    name: 'Wisconsin',
    code: 'WI',
  },
  {
    name: 'Wyoming',
    code: 'WY',
  },
];

export function formatFieldErrors(
  errors: Maybe<any>[],
  { prefix = '', skipCamelCasing = false } = {}
) {
  return errors.reduce((acc: { [key: string]: string }, err: FieldError) => {
    if (err && err.field) {
      const field = err.field;
      if (err && err.errors && err.errors.length && err.errors[0] !== null) {
        const message = err?.errors[0].message;
        if (message) {
          const fieldName = skipCamelCasing ? field : camelCase(field);
          const prefixedFieldName = prefix
            ? `${prefix}.${fieldName}`
            : fieldName;
          acc[prefixedFieldName] = message;
        }
      }
    }
    return acc;
  }, {});
}

export function useMobileWidth() {
  const { innerWidth } = useWindowSize();
  return innerWidth < 1200;
}

export function useTabletWidth() {
  const { innerWidth } = useWindowSize();
  return innerWidth < 992;
}

export function usePhoneXlWidth() {
  const { innerWidth } = useWindowSize();
  return innerWidth < 768;
}

export function safeSum(values: (number | undefined | null)[]): number {
  const filtered: number[] = values.filter(Boolean) as number[];
  return filtered.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );
}
